import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ProjectTitle from "../components/ProjectTitle";
import DetailFacility from "../components/DetailFacility";
import DetailBill from "../components/DetailBill";
import BidCondition from "../pages/BidCondition";
import BidUpload from "../pages/BidUpload";

import { useApi } from "../api/contexts/ApiContext";
import { api_url } from "../api/config/config";
import { DISCOUNT_PLAN_MASTER, DISCOUNT_PLAN_UNIT } from "../config/const";
import { RootState } from "../store";
import { Discount, Facility, OpportunityStructure, BidFormInput } from "../types/type";
import { initialData_plan1 } from "../components/Plan1";
import { initialData_plan2 } from "../components/Plan2";
import { initialData_plan3 } from "../components/Plan3";
import { initialData_plan4 } from "../components/Plan4";
import { formatDateForInput } from "../utils/utils";
import "./Bid.css";

const Bid: React.FC = () => {
    const [isOpenMap, setIsOpenMap] = useState<Record<number, boolean>>({});
    const backendURL = api_url;
    const { userInfo } = useSelector((state: RootState) => state.auth);
    const { vendorInfo } = useSelector((state: RootState) => state.vendor);
    const selectedFacilitiesId = useSelector((state: RootState) => state.selectFacilities.facilitiesId);
    const [loading, setLoading] = useState(true);
    const [bid, setBid] = useState<any>(null);
    const [bidMode, setBidMode] = useState<string>("all");
    const [confirm, setConfirm] = useState(false);
    const [alreadyBid, setAlreadyBid] = useState(false);
    const [opportunity, setOpportunity] = useState<any>();
    const [facilities, setFacilities] = useState<any[]>([]);
    const [recs, setRecs] = useState<{ [key: string]: Facility[] }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [isRemarkConfirmed, setIsRemarkConfirmed] = useState(false);

    const { getModule } = useApi();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const raw = queryParams.get("opportunity_id");
    const opportunityId = raw ? Number(raw) : 0; // ない時は undefined
    useEffect(() => {
        if (opportunityId === 0 || !Number.isFinite(opportunityId)) {
            // おかしくなっているので、リロードでルートへ
            window.location.replace('/');
        }
    }, [location.search]);

    const toggleOpen = (id: number) => {
        setIsOpenMap(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    };


    const methods = useForm<BidFormInput>({
        defaultValues: {
            opportunity_id: opportunityId,
            bid_mode: bidMode,
            status: "1",
            conditions: [],
            vendor_id: vendorInfo?.vendor_id,
            user_id: userInfo?.id,
        },
    });
    const { handleSubmit, control, setValue } = methods;
    const formData = useWatch({ control });
    const fetchInitialData = useCallback(async () => {
        try {
            setLoading(true);
            const [oppRes, facRes, bidRes] = await Promise.all([
                fetch(`${backendURL}/getOpportunity?opportunity_id=${opportunityId}&user_id=${userInfo?.id}`),
                fetch(`${backendURL}/getFacilityByOpportunityId?opportunity_id=${opportunityId}&user_id=${userInfo?.id}`),
                fetch(`${backendURL}/hasAlreadyBid?user_id=${userInfo?.id}&vendor_id=${vendorInfo?.vendor_id}&opportunity_id=${opportunityId}`),
            ]);

            const opportunityData = await oppRes.json();
            const facilityData = await facRes.json();
            const alreadyBidData = await bidRes.json();

            setOpportunity(opportunityData.data);
            setFacilities(facilityData.data);
            setAlreadyBid(alreadyBidData.data);
        } catch (error) {
            console.error("Initialization Error:", error);
        } finally {
            setLoading(false);
        }
    }, [backendURL, opportunityId, userInfo?.id]);

    const fetchBidDetail = useCallback(async () => {
        if (!alreadyBid) return;
        try {
            const res = await fetch(`${backendURL}/getBidById?bid_id=${alreadyBid}`);
            const data = await res.json();
            setBid(data.data);

        } catch (err) {
            console.error("Fetch bid error", err);
        }
    }, [alreadyBid]);

    useEffect(() => {
        if (bid?.mode) {
            setBidMode(bid.mode);
        }
    }, [bid?.mode]);


    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    useEffect(() => {
        fetchBidDetail();
    }, [fetchBidDetail]);

    const getInitialUsage = (contractType: string) => {
        switch (contractType) {
            case "SEASON":
            case "LOW_LOAD_SEASON":
            case "LOW_LOAD_FIXED":
                return initialData_plan1;
            case "LOAD_FACTOR":
            case "LOW_LOAD_STEP":
                return initialData_plan2;
            case "SEASONAL_HOLIDAYS":
                return initialData_plan3;
            case "SEASON_TIME_ZONE":
            case "LOW_LOAD_TIME":
                return initialData_plan4;
            default:
                return initialData_plan1;
        }
    };

    const setConditions = () => {

        if (facilities.length == 0) return;

        const baseConditions = facilities.reduce((acc: any, facility) => {
            //  選択した供給地点以外は除外
            if (selectedFacilitiesId.length > 0 && !selectedFacilitiesId.includes(facility.id)) return acc;

            acc[facility.id] = {
                facility_id: facility.id,
                contract_type: facility.contract_type,
                is_custom: false,
                custom_check_1: false,
                custom_check_2: false,
                custom_check_3: false,
                name: '',
                base: 0,
                annual_bill_total: 0,
                contract_term: 1,
                contract_cancel_by: 1,
                penalty: false,
                penalty_fee: 0,
                penalty_note: '',
                usage: getInitialUsage(facility.contract_type),
                supply_start_date: '',
                application_deadline: '',
                discount: DISCOUNT_PLAN_MASTER[facility.supplier_id]?.map((d: Discount) => ({
                    ...d,
                    target: DISCOUNT_PLAN_UNIT[d.target as keyof typeof DISCOUNT_PLAN_UNIT] || '',
                })) || [],
            };
            return acc;
        }, {});

        if (bid?.conditions?.length) {
            bid.conditions.forEach((condition: any) => {
                if (condition.facility_id && baseConditions[condition.facility_id]) {
                    baseConditions[condition.facility_id] = {
                        ...baseConditions[condition.facility_id],
                        ...condition,
                        supply_start_date: formatDateForInput(condition?.supply_start_date),
                        application_deadline: formatDateForInput(condition?.application_deadline),
                    };
                }
            });
        }

        setValue("conditions", baseConditions, { shouldDirty: false, shouldValidate: false });
    };

    useEffect(() => {
        if (facilities.length > 0) {
            setConditions();
        }
    }, [facilities, bid, selectedFacilitiesId]);

    useEffect(() => {
        if (facilities.length > 0) {
            const tmpRecs: { [key: string]: Facility[] } = {};
            let filtered = facilities;

            if (selectedFacilitiesId.length > 0) {
                filtered = facilities.filter(f => selectedFacilitiesId.includes(f.id));
            }

            filtered.forEach(facility => {
                const supplierName = facility.supplier?.name;
                if (supplierName) {
                    tmpRecs[supplierName] = tmpRecs[supplierName] || [];
                    tmpRecs[supplierName].push(facility);
                }
            });
            setRecs(tmpRecs);
        }
    }, [facilities, selectedFacilitiesId]);

    const handleSubmitBid = async () => {
        if (isSubmitting) return; // 2回押し防止
        setIsSubmitting(true);

        let conditions = Object.values(formData.conditions || {});

        // まとめて応札モードの場合、施設ごとに条件を複製
        if (bidMode === "all" && conditions.length > 1) {
            const conditionMap: { [facilityId: string]: any } = {};
            conditions.forEach((c: any) => {
                if (c.facility_id) {
                    conditionMap[c.facility_id] = c;
                }
            });

            let newConditions: any[] = [];

            Object.values(recs).forEach((facilitiesArr: Facility[]) => {
                if (facilitiesArr.length === 0) return;
                const lastFacility = facilitiesArr[facilitiesArr.length - 1];
                const lastCondition = conditionMap[lastFacility.id];
                if (lastCondition) {
                    facilitiesArr.forEach(facility => {
                        newConditions.push({
                            ...lastCondition,
                            facility_id: facility.id,
                        });
                    });
                }

            });

            conditions = newConditions;
        }

        try {
            if (!userInfo) {
                navigate("/login");
            }

            // --- FormData 構築 ---
            const fd = new FormData();

            // その他情報
            fd.append("bid_id", bid?.id?.toString() ?? "");
            fd.append("bid_mode", bidMode);
            fd.append("user_id", userInfo?.id.toString() ?? "");
            fd.append("vendor_id", vendorInfo?.vendor_id.toString() ?? "");
            fd.append("opportunity_id", opportunityId.toString());

            // conditions は JSON 文字列でまとめて送る
            fd.append("conditions", JSON.stringify(conditions));

            // 選択されたファイルを追加
            if (selectedFiles) {
                for (let i = 0; i < selectedFiles.length; i++) {
                    if (selectedFiles[i].size > 52428800) { // 50MB
                        alert('ファイルサイズは50MBまでです。');
                        return;
                    }
                    if (bid?.files && bid.files.some((file: any) => file.upload_file_name === selectedFiles[i].name)) {
                        alert('ファイル名が重複しています。別の名前に変更してから、再度アップロードしてください。');
                        return;
                    }
                    fd.append("files[]", selectedFiles[i]);
                }
            }

            // --- API 呼び出し ---
            const response = await fetch(`${backendURL}/tenderCopyOrCreateWithFileUpload`, {
                method: "POST",
                body: fd,
            });

            const result = await response.json();

            if (result.status == "success") {
                navigate('/bid_completed');
                return;
            }

            alert('保存処理に失敗しました。');

        } catch (err) {
            alert('保存処理に失敗しました。');
            console.error("Submit Error:", err);
        } finally {
            setIsSubmitting(false);

        }
    };

    const validationConditions = () => {
        // 0) recsに施設が1つも無い → そもそもNG
        const groups = Object.values(recs);
        const hasFacilities = groups.some(arr => arr.length > 0);
        if (!hasFacilities) {
            alert('対象施設が取得できませんでした。画面をリロードしてください');
            return false;
        }

        // 1) conditions の存在チェック（{} / [] / null / undefinedを弾く）
        const conds: any = formData.conditions ?? {};
        const condCount = Array.isArray(conds) ? conds.length : Object.keys(conds).length;
        if (condCount === 0) {
            alert('応札条件を入力してください');
            return false;
        }

        // 2) 画面上の対象施設に対応する「条件そのもの」があるかを先に確認
        const getCond = (id: number) => conds[id] ?? conds[String(id)];

        if (bidMode === 'all') {
            // グループ代表（最後の施設）に1件でも条件が無ければNG
            const hasAnyRep = groups.some(facilities => {
                const last = facilities[facilities.length - 1];
                return last ? !!getCond(last.id) : false;
            });
            if (!hasAnyRep) {
                alert('応札条件を入力してください');
                return false;
            }
        } else {
            // each は各施設に必須
            for (const facilities of groups) {
                for (const f of facilities) {
                    if (!getCond(f.id)) {
                        alert('応札条件を入力してください');
                        return false;
                    }
                }
            }
        }

        // 3) 中身の必須チェック
        if (bidMode === 'all') {
            for (const facilities of groups) {
                const last = facilities[facilities.length - 1];
                if (!last) continue;
                if (!validateSafe(getCond(last.id))) return false;
            }
        } else {
            for (const facilities of groups) {
                for (const f of facilities) {
                    if (!validateSafe(getCond(f.id))) return false;
                }
            }
        }

        return true;
    };

    // discount 未定義でも落ちないように安全化
    const validateSafe = (condition: any) => {
        if (!condition || !condition.name || condition.name.trim() === '') {
            alert('プラン名を入力してください'); return false;
        }
        if (!condition?.supply_start_date || !isValidDateY2K(condition.supply_start_date)) {
            alert('予定供給開始日を正しい日付で入力してください'); return false;
        }

        if (!condition?.application_deadline || !isValidDateY2K(condition.application_deadline)) {
            alert('需給申込書（又は該当資料）の提出期限を正しい日付で入力してください'); return false;
        }

        const discounts = Array.isArray(condition.discount) ? condition.discount : [];
        for (const discount of discounts) {
            if (!discount.type) { alert('割引プランを選択してください'); return false; }
            if (!discount.target) { alert('割引単位を入力してください'); return false; }
            if (discount.value === '' || discount.value === null || discount.value === undefined) {
                alert('割引値を入力してください'); return false;
            }
        }
        return true;
    };

    const isValidDateY2K = (s: unknown): boolean => {
        if (typeof s !== 'string') return false;
        const trimmed = s.trim();
        const m = trimmed.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})/);
        if (!m) return false;

        const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
        if (y < 2010 || y > 2100) return false; // 明らかにおかしな値を弾く。

        // 実在日付判定
        const dt = new Date(y, mo - 1, d);
        return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    };

    const handleBidModeChange = (mode: string) => {
        const message =
            mode === 'all'
                ? 'まとめて応札に切り替えると、施設ごと応札の入力情報が削除されます。本当によろしいですか？'
                : '施設ごと応札に切り替えると、まとめて応札の入力情報が削除されます。本当によろしいですか？';

        const confirmed = window.confirm(message);

        if (!confirmed) {
            return;
        }
        setConditions();
        setIsOpenMap({});
        navigate(`/bid?bid_mode=${mode}&opportunity_id=${opportunityId}`);
        setBidMode(mode);
    };

    const handleConfirmBid = (e: any) => {
        e.preventDefault();
        if (!isRemarkConfirmed) {
            alert('備考欄の内容を確認してください。');
            return;
        }
        if (validationConditions()) {
            setConfirm(true);
        } else {
        }
    };

    const handleBidStart = () => {
        navigate(`/bid_start?opportunity_id=${opportunityId}`);
    }

    if (loading) {
        return (
            <section className="section-name project-details-section">
                <div className="container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: "100%" }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="section-name project-details-section">
                <div className="container">
                    <div className="project-details mb-lg-5 mb-4">
                        {opportunity && <ProjectTitle company={opportunity} />}

                        {!confirm && Object.values(recs).reduce((acc, facilities) => acc + facilities.length, 0) >= 2 && (
                            <div className="mb-lg-5 mb-4 text-center">
                                <button
                                    type="button"
                                    id="goAll"
                                    className={`btn btn-lg btn-theme btn-outline-danger rounded-pill fw-300 mb-lg-0 mb-3 px-lg-5 mr-3 ${bidMode === 'all' ? 'active' : ''}`}
                                    onClick={() => handleBidModeChange('all')}
                                >
                                    <span className="px-lg-4">まとめて応札</span>
                                </button>
                                <button
                                    type="button"
                                    id="goEach"
                                    className={`btn btn-lg btn-theme btn-outline-danger rounded-pill fw-300 mb-lg-0 mb-3 px-lg-5 ${bidMode === 'each' ? 'active' : ''}`}
                                    onClick={() => handleBidModeChange('each')}
                                >
                                    <span className="px-lg-3">施設ごとに応札</span>
                                </button>
                            </div>
                        )}

                        <FormProvider {...methods}>
                            <form id="bid-form" method="post" onSubmit={handleSubmit(handleSubmitBid)}>
                                {Object.entries(recs).map(([supplierName, facilities]) => (
                                    <div className="bg-ass-demo p-4 mb-5" key={supplierName}>
                                        <div className="row">
                                            <div className="col-lg-5 col-md-7 col-10 mx-auto py-3 mb-3">
                                                <div className="bg-white text-center rounded-pill text-sm py-1 fw-300">
                                                    <img className="img img-fluid height-25 mb-1 mr-2" src="assets/images/other/icon-poles.svg" alt="" />
                                                    {supplierName}管轄
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            {bidMode === 'each' ? (
                                                opportunity && facilities.map((facility, index) => (
                                                    <div className="my-1 pt-1 bg-white" key={facility.id}>
                                                        <DetailFacility facility={facility} company={opportunity} isOpen={!isOpenMap[facility.id]}
                                                            onToggle={() => toggleOpen(facility.id)} />
                                                        {!isOpenMap[facility.id] && (
                                                            <div className="bg-white pb-3">
                                                                <DetailBill facility={facility} opportunity={opportunity} isOpen={!isOpenMap[facility.id]} />
                                                            </div>
                                                        )}
                                                        <div className="bg-white pb-3">
                                                            <BidCondition facility={facility} confirm={confirm} />
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="my-1 pt-1 bg-white">
                                                    {opportunity && facilities.map((facility) => (
                                                        <div key={facility.id}>
                                                            <DetailFacility facility={facility} company={opportunity} isOpen={!isOpenMap[facility.id]}
                                                                onToggle={() => toggleOpen(facility.id)} />
                                                            <div className="bg-white pb-3">
                                                                <DetailBill facility={facility} opportunity={opportunity} isOpen={!isOpenMap[facility.id]} />
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <BidCondition facility={facilities[facilities.length - 1]} confirm={confirm} />

                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <BidUpload
                                    opportunity_id={opportunityId}
                                    bid_mode={bidMode}
                                    confirm={confirm}
                                    onFilesSelected={(files) => setSelectedFiles(files)} // ファイルを親で保持
                                    standaloneUpload={false}
                                />
                                {confirm ?
                                    <div className="text-center mb-lg-4 mb-4">
                                        <button
                                            id="goBid"
                                            type="submit"
                                            className="btn btn-lg btn-theme btn-danger rounded-pill px-lg-5 px-3 mr-2"
                                            disabled={isSubmitting}
                                            onClick={handleSubmitBid}
                                        >
                                            <span className="mx-3">{isSubmitting ? '送信中...' : 'この内容で応札する'}</span>
                                        </button>

                                        <button id="goBack" type="button" className="btn btn-lg btn-theme btn-secondary rounded-pill px-lg-5 px-3"><span className="mx-3" onClick={() => setConfirm(false)}>応札条件入力に戻る</span></button>
                                    </div>
                                    : <div className="text-center mb-lg-4 mb-4">
                                        <div className="bid-remark-confirm d-inline-block mb-3">
                                            <input
                                                id="remarkConfirmCheck"
                                                type="checkbox"
                                                className="bid-remark-confirm-input"
                                                checked={isRemarkConfirmed}
                                                onChange={(e) => setIsRemarkConfirmed(e.target.checked)}
                                            />
                                            <label htmlFor="remarkConfirmCheck" className="bid-remark-confirm-label">
                                                <span className="checkbox-icon" aria-hidden="true"></span>
                                                <span className="checkbox-tile fw-400">備考欄の内容を確認しました</span>
                                            </label>
                                        </div>
                                        <div className="mt-3">
                                            <button
                                                id="bidConfirm"
                                                type="button"
                                                className="btn btn-lg btn-theme btn-danger rounded-pill px-lg-5 px-3 mr-2"
                                                onClick={handleConfirmBid}
                                                disabled={!isRemarkConfirmed}
                                                style={!isRemarkConfirmed ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                                            >
                                                <span className="mx-3">応札内容確認へ進む</span>
                                            </button>
                                            {facilities.length > 1 && (
                                                <button id="goBack" type="button" className="btn btn-lg btn-theme btn-secondary rounded-pill px-lg-5 px-3" onClick={handleBidStart}>
                                                    <span className="mx-3">応札条件入力に戻る</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                }
                            </form>
                        </FormProvider>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
};

export default Bid;
